import React, { useEffect, useState, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { FiltersBar, searchToFilters } from '../../../Utils/Filters/Filter'
import { useTranslation } from 'react-i18next'
import { FilterFromDate, FilterToDate } from '../../../Utils/Filters'
import moment from 'moment'
import { getToken } from '../../../../utils/fetchApi'
import { Stack, Paper, TableHead, Table, TableRow, TableBody, TableCell, CircularProgress, Button, LinearProgress, Typography, Box, Grid, TableSortLabel, Tooltip as MuiTooltip, TableContainer } from '@mui/material';
import { ISTableHeaderCell, ISTableRow, ISTableTitle } from '../../../Utils/ISTable'
import { formattedCurrency } from '../../../../utils/StringUtils'
import { FiletypeXls } from 'react-bootstrap-icons'
import { GoalStatsType } from '../../../../reducers/goal/goalStats'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LabelList
} from "recharts"
import { isIconStyle, ISSwitch } from '../../../ISSwitch'
import { LocationOn, Person } from '@mui/icons-material'
import { useThemeContext } from '../../../../theme/ThemeContext'
import { isVendor, SessionState } from '../../../../reducers/auth/session'
import { connect } from 'react-redux'
import { AppState } from '../../../../reducers'

interface ProgressItem {
  codeZone?: string
  codeVendor?: string
  codeClient?: string
  zone?: string
  vendor?: string
  client?: string
  count?: number
  totalGoals?: number
  monthly?: number[]
}

type Aggregated = {
  name: string
  codeVendor?: string
  goal: number
  total: number
  deficit: number
}

interface Props {
  session: SessionState
}

const GoalStatsClientSSE: React.FC<Props> = ({ session }) => {
  const history = useHistory()
  const { t } = useTranslation(['goal', 'person', 'client'])
  const { theme } = useThemeContext()
  const [goalFilters, setGoalFilters] = useState(searchToFilters(history.location.search))
  const [progressList, setProgressList] = useState<ProgressItem>({})
  const [goalStatsPartial, setGoalStatsPartial] = useState<GoalStatsType[]>([])
  const [isFetching, setIsFetching] = useState(false)
  const eventSourceRef = useRef<EventSource | null>(null)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [orderBy, setOrderBy] = useState<keyof GoalStatsType | 'scope' | 'deficit' | 'graph-deficit' | 'graph-total' | null>('codeClient')
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc')
  const [groupBy, setGroupBy] = useState<"zone" | "vendor">(isVendor(session) ? "vendor" : "zone")
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null)


  const initDefaultFilters = {
    from: moment().startOf('month').tz("America/Argentina/Buenos_Aires").format(),
    to: moment().endOf('month').tz("America/Argentina/Buenos_Aires").format(),
    personType: 'C'
  }

  const handleSort = (column: keyof GoalStatsType | 'scope' | 'deficit' | 'graph-deficit' | 'graph-total') => {
    if (orderBy === column) {
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setOrderBy(column)
      setOrderDirection('asc')
    }

  }

  const sortedGoals = [...goalStatsPartial].sort((a, b) => {
    if (!orderBy) return 0

    let aVal: number | string = 0
    let bVal: number | string = 0

    switch (orderBy) {
      case 'scope':
        aVal = Number(a.goal) ? (Number(a.total) / Number(a.goal)) * 100 : 0
        bVal = Number(b.goal) ? (Number(b.total) / Number(b.goal)) * 100 : 0
        break
      case 'deficit':
        aVal = Math.max(Number(a.goal) - Number(a.total), 0)
        bVal = Math.max(Number(b.goal) - Number(b.total), 0)
        break
      case 'graph-deficit':
        const deficitA = Math.max(Number(a.goal) - Number(a.total), 0)
        const deficitB = Math.max(Number(b.goal) - Number(b.total), 0)

        const reachA = Number(a.goal) ? (Number(a.total) / Number(a.goal)) * 100 : 0
        const reachB = Number(b.goal) ? (Number(b.total) / Number(b.goal)) * 100 : 0

        if (groupBy === 'zone') {
          return (Number(a.codeZone ?? 0) - Number(b.codeZone ?? 0)) ||
            (deficitB - deficitA) ||
            (reachB - reachA)
        }
        if (groupBy === 'vendor') {
          return (Number(a.codeVendor ?? 0) - Number(b.codeVendor ?? 0)) ||
            (deficitB - deficitA) ||
            (reachB - reachA)
        }
        break
      case 'graph-total':
        const totalA = Math.max(Number(a.goal) - Number(a.total), 0)
        const totalB = Math.max(Number(b.goal) - Number(b.total), 0)

        const reachTotalA = Number(a.goal) ? (Number(a.total) / Number(a.goal)) * 100 : 0
        const reachTotalB = Number(b.goal) ? (Number(b.total) / Number(b.goal)) * 100 : 0
        if (groupBy === 'zone') {
          return (Number(a.codeZone ?? 0) - Number(b.codeZone ?? 0)) ||
            (reachTotalB - reachTotalA) ||
            (totalB - totalA)
        }
        if (groupBy === 'vendor') {
          return (Number(a.codeVendor ?? 0) - Number(b.codeVendor ?? 0)) ||
            (reachTotalB - reachTotalA) ||
            (totalB - totalA)
        }
        break
      case 'codeZone':
        return orderDirection === 'asc'
          ? (Number(a.codeZone ?? 0) - Number(b.codeZone ?? 0)) ||
          (Number(a.codeVendor ?? 0) - Number(b.codeVendor ?? 0)) ||
          (Number(a.codeClient ?? 0) - Number(b.codeClient ?? 0))
          : (Number(b.codeZone ?? 0) - Number(a.codeZone ?? 0)) ||
          (Number(b.codeVendor ?? 0) - Number(a.codeVendor ?? 0)) ||
          (Number(b.codeClient ?? 0) - Number(a.codeClient ?? 0))
      case 'codeClient':
        return orderDirection === 'asc'
          ? (Number(a.codeClient ?? 0) - Number(b.codeClient ?? 0))
          : (Number(b.codeClient ?? 0) - Number(a.codeClient ?? 0))
      default:
        const rawA = a[orderBy]
        const rawB = b[orderBy]

        aVal = typeof rawA === 'object' && rawA !== null
          ? JSON.stringify(rawA)
          : (rawA as string | number | undefined) ?? 0

        bVal = typeof rawB === 'object' && rawB !== null
          ? JSON.stringify(rawB)
          : (rawB as string | number | undefined) ?? 0
        break
    }


    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return orderDirection === 'asc' ? aVal - bVal : bVal - aVal
    }

    return orderDirection === 'asc'
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal))
  })

  const handleDownload = async () => {
    if (!downloadUrl) return
    try {
      const token = getToken()
      const res = await fetch(`${process.env.REACT_APP_API_URL}${downloadUrl}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      const blob = await res.blob()
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)

      const from = searchToFilters(history.location.search).from
      link.download = `objetivos_clientes_${moment(from).tz("America/Argentina/Buenos_Aires").format('YYYY-MM')}_industrias_saladillo.xlsx`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error("Error descargando Excel", err)
    }
  }


  const startGoalStatsStream = () => {
    setProgressList({})
    setGoalStatsPartial([])
    setDownloadUrl(null)
    setIsFetching(true)

    const params = new URLSearchParams(history.location.search).toString()
    const url = `${process.env.REACT_APP_API_URL}/goals-stats-clients-sse?${params}&access_token=${getToken()}`

    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    const es = new EventSource(url)
    eventSourceRef.current = es

    es.addEventListener("progress", (event) => {
      try {
        const parsed = JSON.parse(event.data)

        const progress: ProgressItem = {
          codeZone: parsed.codeZone,
          codeVendor: parsed.codeVendor,
          codeClient: parsed.codeClient,
          zone: parsed.zone,
          vendor: parsed.vendor,
          client: parsed.client,
          count: parsed.count,
          totalGoals: parsed.totalGoals,
          monthly: parsed.monthly
        }
        setProgressList(progress)

        const goalStats: GoalStatsType = {
          goalData: parsed.goalData,
          codeZone: parsed.codeZone,
          codeVendor: parsed.codeVendor,
          codeClient: parsed.codeClient,
          zone: parsed.zone,
          vendor: parsed.vendor,
          client: parsed.client,
          goal: parsed.goal,
          liquidationTotal: parsed.liquidationTotal,
          total: parsed.total,
          monthly: parsed.monthly
        }

        setGoalStatsPartial((prev) => [...prev, goalStats])

      } catch (err) {
        console.error("Error parseando progress SSE", err)
      }
    })

    es.addEventListener("done", async (event) => {
      try {
        const data = JSON.parse(event.data)
        setProgressList(data)

        const { downloadUrl } = data
        setDownloadUrl(downloadUrl)
        setIsFetching(false)

        es.close()
        eventSourceRef.current = null
      } catch (err) {
        console.error("Error en SSE done", err)
      }
    })

    es.addEventListener("error", (err) => {
      console.error("SSE error", err)
      es.close()
      eventSourceRef.current = null
      setIsFetching(false)
    })
  }

  const stopProcess = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
      setIsFetching(false)
      setProgressList({})
      setGoalStatsPartial([])
    }
  }


  const aggregateBy = (key: "zone" | "vendor") => {
    const map = new Map<string, Aggregated>()

    for (const row of goalStatsPartial) {
      const group = row[key]
      if (!group) continue

      const total = Number(row.total ?? 0)
      const goal = Number(row.goal ?? 0)
      const deficit = Math.max(Number(row.goal ?? 0) - Number(row.total ?? 0), 0)

      if (!map.has(group)) {
        map.set(group, {
          name: group,
          codeVendor: row.codeVendor,
          total, goal, deficit
        })
      } else {
        const agg = map.get(group)!
        agg.total += total
        agg.goal += goal
        agg.deficit += deficit
      }
    }

    return Array.from(map.values())
  }

  const chartData = aggregateBy(groupBy).sort((a, b) => {
    const aCode = parseInt(a.codeVendor ?? "0", 10);
    const bCode = parseInt(b.codeVendor ?? "0", 10);
    return aCode - bCode;
  }).map(row => {
    return {
      ...row,
      scopePercent: row.goal ? ((row.total / row.goal) * 100).toFixed(0) : "0"
    }
  });

  const yAxisFormatter = (value: number) => {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(0)} M`
    if (value >= 1_000) return `${(value / 1_000).toFixed(0)} K`
    return value.toString()
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;

    const data = payload[0].payload;

    return (
      <Paper sx={{ borderColor: theme.palette.success.main, borderStyle: "solid", borderWidth: 1, p: 1.5, maxWidth: 350 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ marginBottom: "15px" }}>
          {data.name}
        </Typography>

        <Grid container spacing={0.5} direction="column">
          <Grid container item justifyContent="space-between">
            <Typography variant="body2">{t("goal")}&nbsp;&nbsp;</Typography>
            <Typography variant="body2" fontWeight="bold">
              {formattedCurrency(data.goal)}
            </Typography>
          </Grid>

          <Grid container item justifyContent="space-between">
            <Typography variant="body2">{t("totalOrders")}&nbsp;&nbsp;</Typography>
            <Typography variant="body2" fontWeight="bold">
              {formattedCurrency(data.total)}
            </Typography>
          </Grid>

          <Grid container item justifyContent="space-between">
            <Typography variant="body2">{t("deficit")}&nbsp;&nbsp;</Typography>
            <Typography variant="body2" fontWeight="bold">
              {formattedCurrency(Number(data.deficit ?? 0))}
            </Typography>
          </Grid>

          <Grid container item justifyContent="space-between">
            <Typography variant="body2">{t("scope")}&nbsp;&nbsp;</Typography>
            <Typography variant="body2" fontWeight="bold">
              {data.goal
                ? ((data.total / data.goal) * 100).toFixed(2) + "%"
                : "0%"}
            </Typography>
          </Grid>
          {data.dataAdic && (
            <Grid container item justifyContent="space-between">
              <Typography variant="body2">{t("dataAdic")}&nbsp;&nbsp;</Typography>
              <Typography variant="body2" fontWeight="bold">
                {data.dataAdic}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };



  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [])

  useEffect(() => {
    const onSearchChange = () => {
      setGoalFilters(searchToFilters(history.location.search))
    }
    onSearchChange()
  }, [history.location.search])


  const tableBodyRef = useRef<HTMLTableSectionElement | null>(null)

  useEffect(() => {
    if (selectedGroup && tableBodyRef.current) {

      const row = tableBodyRef.current.querySelector<HTMLTableRowElement>(
        `[data-group='${selectedGroup}']`
      )
      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [selectedGroup])


  return (
    <Stack style={{ display: 'flex', flexDirection: 'column', maxHeight: 'calc(100% - var(--header-height)' }}>
      <Stack display="flex" flexDirection='row' justifyContent="normal" alignItems="center" flexWrap="wrap" spacing={1}>
        <FiltersBar defaultFilters={initDefaultFilters}>
          <FilterFromDate name="from" label={t("from")} />
          <FilterToDate name="to" label={t("to")} />
        </FiltersBar>
        <Stack display="flex" flexDirection='row' alignItems="baseline" flexWrap="wrap" spacing={1}>
          <Button
            id="download"
            aria-label="download"
            size='large'
            style={{ marginRight: '20px', textTransform: 'none' }}
            onClick={startGoalStatsStream}
            disabled={isFetching || !goalFilters.personType}>
            {t('generate')}
          </Button>

          <Button
            style={{ marginRight: '20px', textTransform: 'none' }}
            onClick={stopProcess}
            size='large'
            disabled={!isFetching}
          >
            {t('cancel')}
          </Button>

          <Button
            variant="contained"
            onClick={handleDownload}
            size='large'
            style={{ marginRight: '20px', textTransform: 'none' }}
            disabled={!downloadUrl}
          >
            {t('downloadReport')}&nbsp;&nbsp;<FiletypeXls size={25} />
          </Button>
        </Stack>
      </Stack>

      <Paper style={{ borderRadius: "10px", margin: "10px", padding: "10px" }}>

        <Box>
          <Grid container spacing={2} alignItems="center">
            {!isVendor(session) && (
              <Grid item xs={4}>
                <Typography component='span'>{t('zone')}:&nbsp;&nbsp;</Typography>
                <Typography component='span' sx={{ fontWeight: 'bold' }}>{progressList.codeZone ? `${progressList.codeZone} - ${progressList.zone}` : ''}</Typography>
              </Grid>)}
            <Grid item xs={4}>
              <Typography component='span'>{t('vendor')}:&nbsp;&nbsp;</Typography>
              <Typography component='span' sx={{ fontWeight: 'bold' }}>{progressList.codeVendor ? `${progressList.codeVendor} - ${progressList.vendor}` : ''}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography component='span'>{t('client')}:&nbsp;&nbsp;</Typography>
              <Typography component='span' sx={{ fontWeight: 'bold' }}>{progressList.codeClient ? `${progressList.codeClient} - ${progressList.client}` : ''}</Typography>
            </Grid>

            {progressList.count != null && progressList.totalGoals != null && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box flexGrow={1}>
                    <LinearProgress
                      color='success'
                      variant="determinate"
                      value={(progressList.count / progressList.totalGoals) * 100}
                      style={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ minWidth: 50 }}>
                    {progressList.count}/{progressList.totalGoals}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        {isFetching &&
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
            <CircularProgress size={25} />
          </div>
        }
      </Paper>

      < Paper style={{ borderRadius: "10px", margin: "10px", padding: "20px" }}>
        <Box display="flex" justifyContent="flex-start" alignItems={"center"} mb={2}>
          {!isVendor(session) && (
            <>
              <Typography variant="body1">{t("graph")}</Typography>
              <Typography variant="body1" sx={{ ml: 1, mr: 1 }}>{t("zone")}</Typography>
              <ISSwitch
                checked={groupBy === "vendor"}
                onChange={() => setGroupBy(groupBy === "zone" ? "vendor" : "zone")}
                icon={<LocationOn sx={isIconStyle(theme)} />}
                checkedIcon={<Person sx={isIconStyle(theme)} />}
              />
              <Typography variant="body1" sx={{ ml: 1 }}>{t("vendor")}</Typography>
            </>
          )}
        </Box>
        <ResponsiveContainer width="100%" height={300}>
          {chartData.length > 0 ? (
            <BarChart data={chartData}>
              <XAxis
                dataKey="name"
                angle={-30}
                textAnchor="end"
                interval={0}
                tick={({ x, y, payload }) => {
                  if (groupBy === 'vendor') return <></>
                  const words = payload.value.split(" ");
                  return (
                    <g transform={`translate(${x},${y + 10})`}>
                      {words.map((word, index) => (
                        <text
                          key={index}
                          fill={theme.palette.text.secondary}
                          x={0}
                          y={index * 14}
                          textAnchor="middle"
                          fontSize={12}
                        >
                          {word}
                        </text>
                      ))}
                    </g>
                  );
                }} />

              <YAxis tickFormatter={yAxisFormatter} />
              <Tooltip cursor={{ fill: theme.palette.action.hover }} content={<CustomTooltip />} />

              <Legend
                verticalAlign="top"
                align="right"
                layout="horizontal"
                wrapperStyle={{ top: 0, right: 0 }}
                formatter={(value) => {
                  if (value === 'total') return (t('totalOrders'))
                  return (t(value))
                }}
              />
              <Bar dataKey="total" stackId="a" fill="#008457"
                onClick={(data) => {
                  setSelectedGroup(data.name ?? '')
                  setOrderBy('graph-total')
                  setOrderDirection('desc')
                }}
              >
                <LabelList
                  dataKey="scopePercent"
                  content={(props: any) => {
                    const { x, y, width, value } = props;
                    return (
                      <text
                        x={x + width / 2}
                        y={y + 15}
                        fill="#fff"
                        textAnchor="middle"
                        fontSize={12}
                      >
                        {value}%
                      </text>
                    );
                  }}
                />
              </Bar>

              <Bar dataKey="deficit" stackId="a" fill="#d32f2f"
                onClick={(data) => {
                  setSelectedGroup(data.name ?? '')
                  setOrderBy('graph-deficit')
                  setOrderDirection('desc')
                }}
              >
              </Bar>

            </BarChart>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography variant="body2" color="text.secondary">
                {t("Sin Datos")}
              </Typography>
            </Box>
          )}
        </ResponsiveContainer>
      </Paper >

      <Stack style={{ display: 'flex', flexDirection: 'column', height: '75%', borderRadius: "10px", margin: "10px", minWidth: "500px" }}>
        <ISTableTitle title={t('title')} />
        <TableContainer sx={{ overflowX: 'auto', height: '100%' }}>
          <Table stickyHeader size="medium" >
            <TableHead>
              <TableRow selected={true}>
                {!isVendor(session) && (

                  <ISTableHeaderCell sortDirection={orderBy === 'zone' || !orderBy ? orderDirection : false}
                    sx={{ position: 'sticky', top: 0, zIndex: 10 }}
                  >
                    <TableSortLabel
                      active={orderBy === 'zone' || !orderBy}
                      direction={orderBy === 'zone' || !orderBy ? orderDirection : 'asc'}
                      onClick={() => handleSort('codeZone')}
                    >
                      {t('zone')}
                    </TableSortLabel>
                  </ISTableHeaderCell>)}
                <ISTableHeaderCell sortDirection={orderBy === 'vendor' ? orderDirection : false}
                  sx={{ position: 'sticky', top: 0, zIndex: 10 }}
                >
                  <TableSortLabel
                    active={orderBy === 'vendor'}
                    direction={orderBy === 'vendor' ? orderDirection : 'asc'}
                    onClick={() => handleSort('codeVendor')}
                  >
                    {t('vendor')}
                  </TableSortLabel>
                </ISTableHeaderCell>

                <ISTableHeaderCell align='right' sortDirection={orderBy === 'codeClient' ? orderDirection : false}
                  style={{ position: 'sticky', top: 0, zIndex: 10 }}
                >

                  <TableSortLabel
                    active={orderBy === 'codeClient'}
                    direction={orderBy === 'codeClient' ? orderDirection : 'asc'}
                    onClick={() => handleSort('codeClient')}
                    style={{ maxWidth: '30px' }}
                  >
                    {t('client')}
                  </TableSortLabel>
                </ISTableHeaderCell>
                <ISTableHeaderCell sortDirection={orderBy === 'client' ? orderDirection : false}
                  sx={{ position: 'sticky', top: 0, zIndex: 10 }}
                >

                  <TableSortLabel
                    active={orderBy === 'client'}
                    direction={orderBy === 'client' ? orderDirection : 'asc'}
                    onClick={() => handleSort('client')}
                  >
                    {t('name', { ns: 'client' })}
                  </TableSortLabel>
                </ISTableHeaderCell>
                <ISTableHeaderCell>
                  {t('dataAdic')}
                </ISTableHeaderCell>
                <ISTableHeaderCell align='right' sortDirection={orderBy === 'scope' ? orderDirection : false}
                  sx={{ position: 'sticky', top: 0, zIndex: 10 }}
                >

                  <TableSortLabel
                    active={orderBy === 'scope'}
                    direction={orderBy === 'scope' ? orderDirection : 'asc'}
                    onClick={() => handleSort('scope')}
                  >
                    {t('scope')}
                  </TableSortLabel>
                </ISTableHeaderCell>
                <ISTableHeaderCell align='right' sortDirection={orderBy === 'deficit' ? orderDirection : false}
                  sx={{ position: 'sticky', top: 0, zIndex: 10 }}
                >

                  <TableSortLabel
                    active={orderBy === 'deficit'}
                    direction={orderBy === 'deficit' ? orderDirection : 'asc'}
                    onClick={() => handleSort('deficit')}
                  >
                    {t('deficit')}
                  </TableSortLabel>
                </ISTableHeaderCell>
                <ISTableHeaderCell align='right' sortDirection={orderBy === 'liquidationTotal' ? orderDirection : false}
                  sx={{ position: 'sticky', top: 0, zIndex: 10 }}
                >
                  <TableSortLabel
                    active={orderBy === 'liquidationTotal'}
                    direction={orderBy === 'liquidationTotal' ? orderDirection : 'asc'}
                    onClick={() => handleSort('liquidationTotal')}
                  >
                    {t('liquidationOrders')}
                  </TableSortLabel>
                </ISTableHeaderCell>
                <ISTableHeaderCell align='right' sortDirection={orderBy === 'total' ? orderDirection : false}
                  sx={{ position: 'sticky', top: 0, zIndex: 10 }}
                >

                  <TableSortLabel
                    active={orderBy === 'total'}
                    direction={orderBy === 'total' ? orderDirection : 'asc'}
                    onClick={() => handleSort('total')}
                  >
                    {t('totalOrders')}
                  </TableSortLabel>
                </ISTableHeaderCell>
                <ISTableHeaderCell align='right' sortDirection={orderBy === 'goal' ? orderDirection : false}
                  sx={{ position: 'sticky', top: 0, zIndex: 10 }}
                >

                  <TableSortLabel
                    active={orderBy === 'goal'}
                    direction={orderBy === 'goal' ? orderDirection : 'asc'}
                    onClick={() => handleSort('goal')}
                  >
                    {t('goal')}
                  </TableSortLabel>
                </ISTableHeaderCell>
              </TableRow>
            </TableHead>

            <TableBody ref={tableBodyRef}>
              {sortedGoals.map((row, index) => {
                const isSelected = groupBy === "zone"
                  ? row.zone === selectedGroup
                  : row.vendor === selectedGroup

                return (
                  <ISTableRow
                    key={index}
                    data-group={isSelected ? selectedGroup : ''}
                    color='default'
                    sx={{
                      backgroundColor: isSelected ? "rgba(25, 118, 210, 0.2)" : "inherit"
                    }}
                  >
                    {!isVendor(session) && (
                      <TableCell style={{ maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <MuiTooltip placement='top' title={`${row.codeZone} - ${row.zone}`}>
                          <span>{row.zone}</span>
                        </MuiTooltip>
                      </TableCell>
                    )}
                    <TableCell style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <MuiTooltip placement='top' title={`${row.codeVendor} - ${row.vendor}`}>
                        <span>{row.vendor}</span>
                      </MuiTooltip>
                    </TableCell>
                    <TableCell style={{ maxWidth: '30px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} align='right'>
                      {row.codeClient}
                    </TableCell>
                    <TableCell style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <MuiTooltip placement='top' title={row.client}>
                        <span>{row.client}</span>
                      </MuiTooltip>
                    </TableCell>
                    <TableCell style={{ minWidth: '40px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <MuiTooltip placement='top' title={row.goalData.dataAdic}>
                        <span>{row.goalData.dataAdic}</span>
                      </MuiTooltip>
                    </TableCell>
                    <TableCell align='right' sx={{ width: '82px', fontWeight: (Number(row.total) > Number(row.goal)) ? 'bold' : 'inherit', backgroundColor: (Number(row.total) > Number(row.goal)) ? '#008457' : '#d32f2f' }}>{(Number(row.goal) ? (Number(row.total) / Number(row.goal)) * 100 : 0).toFixed(2)}%</TableCell>
                    <TableCell align='right'>{formattedCurrency(Number(row.goal) - Number(row.total) < 0 ? 0 : Number(row.goal) - Number(row.total))}</TableCell>
                    <TableCell align='right'>{formattedCurrency(row.liquidationTotal)}</TableCell>
                    <TableCell align='right'>{formattedCurrency(row.total)}</TableCell>
                    <TableCell align='right'>{formattedCurrency(row.goal)}</TableCell>

                  </ISTableRow>

                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {isFetching &&
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
            <CircularProgress size={25} />
          </div>
        }
      </Stack>

    </Stack>
  )
}

const mapStateToProps = (state: AppState) => ({
  session: state.session
})

export default connect<Props, {}, {}, AppState>(mapStateToProps, {})(GoalStatsClientSSE)

